import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Save, Building2, FileText } from 'lucide-react';

const SettingsPage = () => {
  const { user, updateCompany } = useAuth();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    company_name: user?.company_name || '',
    company_address: user?.company_address || '',
    gstin: user?.gstin || '',
    contact: user?.contact || '',
    logo_url: user?.logo_url || '',
    invoice_prefix: user?.invoice_prefix || '',
  });

  // Generate default prefix from company name
  const generateDefaultPrefix = (companyName) => {
    if (!companyName) return 'INV';
    const words = companyName.toUpperCase().split(' ');
    if (words.length >= 1) {
      let prefix = words[0].slice(0, 2);
      for (let i = 1; i < words.length; i++) {
        if (words[i]) prefix += words[i][0];
      }
      return prefix;
    }
    return 'INV';
  };

  const defaultPrefix = generateDefaultPrefix(formData.company_name);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateCompany(formData);
      toast.success('Company settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-zinc-900">Settings</h1>
        <p className="text-zinc-500 mt-1">Manage your company profile</p>
      </div>

      {/* Company Settings */}
      <div className="max-w-2xl">
        <div className="bg-white rounded-xl border border-zinc-200 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-zinc-500" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-semibold">Company Profile</h2>
              <p className="text-sm text-zinc-500">This information appears on your invoices</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name</Label>
              <Input
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                placeholder="Your Company LLP"
                data-testid="settings-company-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company_address">Company Address</Label>
              <Input
                id="company_address"
                name="company_address"
                value={formData.company_address}
                onChange={handleChange}
                placeholder="123 Business St, City, State, Country, Pincode"
                data-testid="settings-company-address-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="gstin">GSTIN / Tax ID</Label>
                <Input
                  id="gstin"
                  name="gstin"
                  value={formData.gstin}
                  onChange={handleChange}
                  placeholder="08AAHF03607N1ZX"
                  className="font-mono"
                  data-testid="settings-gstin-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact">Contact Number</Label>
                <Input
                  id="contact"
                  name="contact"
                  value={formData.contact}
                  onChange={handleChange}
                  placeholder="+91-9876543210"
                  data-testid="settings-contact-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="logo_url">Logo URL</Label>
              <Input
                id="logo_url"
                name="logo_url"
                value={formData.logo_url}
                onChange={handleChange}
                placeholder="https://example.com/logo.png"
                data-testid="settings-logo-input"
              />
              <p className="text-xs text-zinc-500">
                Enter a URL to your company logo. It will appear on your invoices.
              </p>
            </div>

            {formData.logo_url && (
              <div className="p-4 bg-zinc-50 rounded-lg">
                <p className="text-sm text-zinc-500 mb-2">Preview:</p>
                <img 
                  src={formData.logo_url} 
                  alt="Logo preview" 
                  className="h-12 object-contain"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}

            {/* Invoice Prefix Section */}
            <div className="pt-4 mt-4 border-t border-zinc-200">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-4 h-4 text-zinc-500" />
                <h3 className="font-medium text-zinc-900">Invoice Settings</h3>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="invoice_prefix">Invoice Number Prefix</Label>
                <Input
                  id="invoice_prefix"
                  name="invoice_prefix"
                  value={formData.invoice_prefix}
                  onChange={handleChange}
                  placeholder={defaultPrefix}
                  className="font-mono uppercase"
                  data-testid="settings-invoice-prefix-input"
                />
                <p className="text-xs text-zinc-500">
                  Custom prefix for invoice numbers. Format: <span className="font-mono">{formData.invoice_prefix || defaultPrefix}/YEAR/MONTH/##</span>
                </p>
                <p className="text-xs text-zinc-400">
                  Leave empty to auto-generate from company name (e.g., "OUTBOUND CLICK LLP" → "OBLLP")
                </p>
              </div>
            </div>

            <div className="pt-4">
              <Button 
                type="submit" 
                disabled={saving}
                className="bg-zinc-900 hover:bg-zinc-800"
                data-testid="save-settings-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </div>

        {/* Account Info */}
        <div className="bg-white rounded-xl border border-zinc-200 p-8 mt-6">
          <h2 className="font-heading text-lg font-semibold mb-4">Account</h2>
          <div className="space-y-3">
            <div className="flex justify-between py-3 border-b border-zinc-100">
              <span className="text-zinc-500">Email</span>
              <span className="font-medium text-zinc-900">{user?.email}</span>
            </div>
            <div className="flex justify-between py-3">
              <span className="text-zinc-500">User ID</span>
              <span className="font-mono text-sm text-zinc-600">{user?.id}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
